import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { generateActionPlanPdf } from "@/utils/generateActionPlanPdf";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Rocket,
  LayoutDashboard,
  Globe,
  Send,
  Sparkles,
  CheckCircle2,
  Circle,
  ArrowRight,
  Target,
  Clock,
  Zap,
  TrendingUp,
  Users,
  Eye,
  MessageSquare,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const INITIAL_ACTIONS = [
  { id: 1, label: "Créer vos profils sur Google Business et les réseaux sociaux clés", priority: "haute", category: "Visibilité", icon: Eye, done: false },
  { id: 2, label: "Définir votre offre phare avec un tarif clair et attractif", priority: "haute", category: "Commercial", icon: Target, done: false },
  { id: 3, label: "Identifier et contacter vos 10 premiers prospects idéaux", priority: "haute", category: "Acquisition", icon: Users, done: false },
  { id: 4, label: "Mettre en place un système de suivi client simple (CRM ou tableur)", priority: "moyenne", category: "Organisation", icon: Clock, done: false },
  { id: 5, label: "Planifier votre première campagne de communication locale", priority: "moyenne", category: "Marketing", icon: TrendingUp, done: false },
  { id: 6, label: "Ouvrir un compte bancaire professionnel et paramétrer la comptabilité", priority: "haute", category: "Finance", icon: Zap, done: false },
  { id: 7, label: "Rédiger 3 contenus de présentation (pitch, page web, plaquette)", priority: "moyenne", category: "Communication", icon: Globe, done: false },
];

const MyActionPlanPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [actions, setActions] = useState(INITIAL_ACTIONS);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [projectName, setProjectName] = useState("");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("onboarding_projects")
      .select("project_name, description, sector, main_challenge")
      .eq("user_id", user.id)
      .eq("completed", true)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.project_name) setProjectName(data.project_name);
      });
  }, [user]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const toggleAction = (id: number) => {
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, done: !a.done } : a)));
  };

  const doneCount = actions.filter((a) => a.done).length;
  const progress = Math.round((doneCount / actions.length) * 100);

  const handleSend = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = input.trim();
    setInput("");
    const newMessages: ChatMessage[] = [...messages, { role: "user", content: userMsg }];
    setMessages(newMessages);
    setStreaming(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/functions/v1/grafton-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${""}`,
          },
          body: JSON.stringify({
            messages: [
              {
                role: "system",
                content: `Tu es Grafton IA, stratège d'entreprise. L'utilisateur te consulte sur son plan d'actions pour "${projectName}". Réponds de manière structurée avec des actions concrètes et priorisées. Utilise le markdown.`,
              },
              ...newMessages,
            ],
          }),
        }
      );

      if (!res.ok) throw new Error("Erreur API");
      if (!res.body) throw new Error("No stream");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      let buffer = "";
      setMessages([...newMessages, { role: "assistant", content: "" }]);

      const flushToken = (token: string) => {
        assistantContent += token;
        setMessages([...newMessages, { role: "assistant", content: assistantContent }]);
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (!line.startsWith("data: ")) continue;
          const d = line.slice(6).trim();
          if (d === "[DONE]") break;
          try {
            const parsed = JSON.parse(d);
            const token = parsed.choices?.[0]?.delta?.content || "";
            if (token) flushToken(token);
          } catch {}
        }
        // Small delay for smoother visual rendering
        await new Promise(r => setTimeout(r, 15));
      }
      if (!assistantContent) {
        const text = await res.text();
        try {
          const j = JSON.parse(text);
          assistantContent = j.choices?.[0]?.message?.content || j.text || text;
        } catch {
          assistantContent = text;
        }
        setMessages([...newMessages, { role: "assistant", content: assistantContent }]);
      }
    } catch {
      setMessages([...newMessages, { role: "assistant", content: "Désolé, une erreur est survenue. Réessayez." }]);
    } finally {
      setStreaming(false);
    }
  };

  const priorityColor = (p: string) => {
    if (p === "haute") return "bg-red-500/10 text-red-500 border-red-500/20";
    if (p === "moyenne") return "bg-amber-500/10 text-amber-500 border-amber-500/20";
    return "bg-green-500/10 text-green-500 border-green-500/20";
  };


  const [generatingImage, setGeneratingImage] = useState(false);

  const generatePdf = async () => {
    setGeneratingImage(true);
    toast.info("Génération de l'image thématique en cours…");
    
    let headerImage: string | undefined;
    try {
      const imgRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/functions/v1/grafton-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${""}`,
          },
          body: JSON.stringify({ sector: actions[0]?.category || "business", projectName }),
        }
      );
      const imgData = await imgRes.json();
      if (imgData.image) headerImage = imgData.image;
    } catch {
      console.warn("Image generation failed, using fallback");
    }
    setGeneratingImage(false);

    const blob = generateActionPlanPdf({
      projectName,
      actions,
      doneCount,
      progress,
      headerImage,
    });
    const url = URL.createObjectURL(blob);
    setPdfUrl(url);
    toast.success("Votre plan d'actions premium a été généré !");

    // Grafton asks for feedback
    const feedbackMsg: ChatMessage = {
      role: "assistant",
      content: `## 📄 Votre plan d'actions est prêt !\n\nJ'ai généré votre document professionnel avec :\n\n- **Page de garde** personnalisée à votre projet\n- **Tableau de bord visuel** avec vos KPIs et votre progression\n- **Détail complet** de chaque action avec priorités\n\nEst-ce que le rendu vous convient ? Souhaitez-vous que je modifie quelque chose ?\n\nPar exemple :\n- Ajouter ou reformuler des actions\n- Changer les priorités\n- Préciser certains objectifs`,
    };
    setMessages((prev) => [...prev, feedbackMsg]);
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top action bar */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xs">G</span>
            </div>
            <span className="text-xs text-muted-foreground">Plan d'actions — Grafton IA</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="hero-outline" size="sm" onClick={() => navigate("/dashboard")}>
              <LayoutDashboard className="h-4 w-4 mr-1" />
              Dashboard
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open("https://grafton-starter.tiiny.site/", "_blank")}
            >
              <Rocket className="h-4 w-4 mr-1" />
              Mise à niveau
            </Button>
            <Button variant="hero" size="sm" onClick={generatePdf} disabled={generatingImage}>
              {generatingImage ? <Sparkles className="h-4 w-4 mr-1 animate-spin" /> : <Globe className="h-4 w-4 mr-1" />}
              {generatingImage ? "Génération…" : "Publier"}
            </Button>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      {pdfUrl && (
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-foreground">📄 Votre plan d'actions est prêt</p>
              <div className="flex gap-2">
                <a href={pdfUrl} download="plan-actions-grafton.pdf">
                  <Button variant="hero" size="sm">Télécharger le PDF</Button>
                </a>
                <Button variant="ghost" size="sm" onClick={() => setPdfUrl(null)}>Fermer</Button>
              </div>
            </div>
            <iframe src={pdfUrl} className="w-full h-[500px] rounded-xl border border-border" />
          </div>
        </div>
      )}

      <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT — Action plan */}
        <div className="lg:col-span-3 space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-5 w-5 text-primary" />
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                Plan d'actions
              </h1>
            </div>
            <p className="text-muted-foreground text-sm">
              {projectName ? `Les actions prioritaires pour ${projectName}` : "Vos actions prioritaires identifiées par Grafton IA"}
            </p>
          </div>

          {/* Progress */}
          <Card className="bg-card border-border">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Progression</span>
                <span className="text-sm font-bold text-primary">{progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {doneCount} / {actions.length} actions complétées
              </p>
            </CardContent>
          </Card>

          {/* Actions list */}
          <div className="space-y-3">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={() => toggleAction(action.id)}
                  className={`w-full flex items-start gap-4 p-4 rounded-xl border transition-all text-left group ${
                    action.done
                      ? "border-green-500/20 bg-green-500/5 opacity-70"
                      : "border-border bg-card hover:border-primary/30 hover:shadow-md hover:shadow-primary/5"
                  }`}
                >
                  <div className="mt-0.5">
                    {action.done ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${action.done ? "line-through text-muted-foreground" : "text-foreground"}`}>
                      {action.label}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge variant="outline" className={`text-[10px] ${priorityColor(action.priority)}`}>
                        {action.priority}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Icon className="h-3 w-3" />
                        {action.category}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* RIGHT — Chat with Grafton */}
        <div className="lg:col-span-2">
          <Card className="bg-card border-border h-full flex flex-col">
            <CardHeader className="pb-3 border-b border-border">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-primary" />
                Discuter avec Grafton IA
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Posez vos questions, exprimez vos problématiques
              </p>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[500px]">
                {messages.length === 0 && (
                  <div className="text-center py-8 space-y-3">
                    <Sparkles className="h-8 w-8 text-primary/30 mx-auto" />
                    <p className="text-sm text-muted-foreground">
                      Décrivez votre problématique et Grafton vous proposera des actions concrètes.
                    </p>
                    <div className="space-y-2">
                      {[
                        "Comment trouver mes premiers clients ?",
                        "Quelle stratégie de prix adopter ?",
                        "Comment me différencier de la concurrence ?",
                      ].map((q, i) => (
                        <button
                          key={i}
                          onClick={() => { setInput(q); }}
                          className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-border hover:border-primary/30 hover:bg-primary/5 text-muted-foreground transition-colors"
                        >
                          <ArrowRight className="h-3 w-3 inline mr-1.5 text-primary" />
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground"
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        msg.content
                      )}
                    </div>
                  </div>
                ))}
                {streaming && (
                  <div className="flex justify-start">
                    <div className="flex gap-1 px-4 py-3">
                      <span className="h-2 w-2 rounded-full bg-primary animate-bounce" />
                      <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:0.15s]" />
                      <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:0.3s]" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                    placeholder="Votre question ou problématique…"
                    className="flex-1 h-10 rounded-lg border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <Button
                    variant="hero"
                    size="icon"
                    onClick={handleSend}
                    disabled={!input.trim() || streaming}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MyActionPlanPage;
